import { Build, CommandJob, Job, Step } from "~/components/build/Show/lib/types";
import { QueryClient, queryOptions, UseQueryOptions } from "@tanstack/react-query";
import fetcher from "~/components/build/Show/lib/fetcher";
import { jobQueryKey, setJobQueryData } from "~/components/build/Show/lib/queries/job";
import { jobRetriesQueryKey } from "~/components/build/Show/lib/queries/jobRetries";
import { stepQueryKey } from "~/components/build/Show/lib/queries/step";
import { isParallelCommandStep, isMatrixCommandStep } from "~/lib/pipeline";
import { unionWith, groupBy } from "lodash";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";

export const stepJobsQueryKey = (build: Build, stepUuid: Step["uuid"]) => {
  return ["build", build.id, "steps", stepUuid, "jobs"];
};

export const stepJobsQueryOptions = (
  build: Build,
  stepUuid: Step["uuid"],
  queryOptionsArgs: Omit<UseQueryOptions<Job[]>, "queryKey" | "queryFn"> = {},
) => {
  return queryOptions({
    queryKey: stepJobsQueryKey(build, stepUuid),
    queryFn: ({ client: queryClient, signal }) =>
      fetchStepJobs(build.buildDataBasePath, stepUuid, signal).then((jobs) => {
        // Set jobs in useBuildStore
        useBuildStore.getState().setJobs(jobs);

        // Set individual job data
        jobs.forEach((job) => setJobQueryData(build, job, queryClient));

        // Populate job and job retries queries from results
        populateJobQueryCache(queryClient, build, stepUuid, jobs);

        return jobs;
      }),
    ...queryOptionsArgs,
  });
};

function populateJobQueryCache(
  queryClient: QueryClient,
  build: Build,
  stepUuid: Step["uuid"],
  jobs: Job[],
) {
  if (jobs.length === 0) {
    return;
  }

  // Prefer step properties from the cache to determine the step type, matching
  // how StepDrawer.tsx makes the same decision via isParallelCommandStep/isMatrixCommandStep.
  // Fall back to checking job properties if the step query hasn't resolved yet.
  const step = queryClient.getQueryData<Step>(stepQueryKey(build, stepUuid));
  const firstJob = jobs[0] as CommandJob;
  const isParallel = step ? isParallelCommandStep(step) : firstJob.parallelGroupIndex != null;
  const isMatrix = step ? isMatrixCommandStep(step) : firstJob.matrix != null;

  if (isParallel) {
    parallelStepJobsGroupedByParallelIndex(jobs as CommandJob[]).forEach((retries) => {
      retries.forEach((job: CommandJob) => {
        queryClient.setQueryData(jobQueryKey(build, job.id), job);
        queryClient.setQueryData(jobRetriesQueryKey(build, job.id), retries);
      });
    });
  } else if (isMatrix) {
    matrixStepJobsGroupedByMatrixDimension(jobs as CommandJob[]).forEach((retries) => {
      retries.forEach((job: CommandJob) => {
        queryClient.setQueryData(jobQueryKey(build, job.id), job);
        queryClient.setQueryData(jobRetriesQueryKey(build, job.id), retries);
      });
    });
  } else {
    jobs.forEach((job) => {
      queryClient.setQueryData(jobQueryKey(build, job.id), job);
      queryClient.setQueryData(jobRetriesQueryKey(build, job.id), jobs);
    });
  }
}

function parallelStepJobsGroupedByParallelIndex(jobs: CommandJob[]): Array<CommandJob[]> {
  return Object.values(groupBy(jobs, (job: CommandJob) => job.parallelGroupIndex)) as Array<
    CommandJob[]
  >;
}

function matrixStepJobsGroupedByMatrixDimension(jobs: CommandJob[]): Array<CommandJob[]> {
  return Object.values(groupBy(jobs, (job: CommandJob) => JSON.stringify(job.matrix))) as Array<
    CommandJob[]
  >;
}

export function setStepJobsQueryData(
  build: Build,
  stepUuid: Step["uuid"],
  jobs: Job[],
  queryClient: QueryClient,
) {
  queryClient.setQueryData<Job[]>(stepJobsQueryKey(build, stepUuid), (prevData) => {
    // No previous data, return the incoming jobs.
    if (prevData === undefined) {
      return jobs;
    }
    // Existing jobs exists, merge the two collections together.
    return unionWith(prevData, jobs, (value, other) => value.id === other.id);
  });
}

function fetchStepJobs(
  buildDataBasePath: Build["buildDataBasePath"],
  stepUuid: Step["uuid"],
  signal: AbortSignal,
) {
  const url = new URL(`${buildDataBasePath}/steps/${stepUuid}/jobs`, window.location.origin);

  return fetcher<Job[]>(url, true, { signal });
}
